import { Video, FileText, ClipboardCheck, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { ScheduledCall, UserProject } from '@teachur/http-client';
import {
  timestampToDate,
  formatTime as formatTimeFromDate,
  formatFullDate as formatFullDateFromDate,
} from '@new/common/lib/dateTime';
import { UpcomingSession, DashboardListItem } from '../types';
import { MILLISECONDS_PER_DAY } from '../constants';

export const formatSessionTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60 && diffMins > 0) return `In ${diffMins} minutes`;
  if (diffHours < 24 && diffHours > 0) return `In ${diffHours} hours`;
  if (diffDays === 0)
    return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  if (diffDays === 1)
    return `Tomorrow at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const getSessionTypeIcon = (type: UpcomingSession['type']) => {
  switch (type) {
    case 'video_call':
      return Video;
    case 'exam':
      return FileText;
    case 'assignment':
      return ClipboardCheck;
    default:
      return Video;
  }
};

export const getSessionTypeColor = (type: UpcomingSession['type']): string => {
  switch (type) {
    case 'video_call':
      return 'bg-sky-100 text-sky-600';
    case 'exam':
      return 'bg-orange-100 text-orange-600';
    case 'assignment':
      return 'bg-purple-100 text-purple-600';
    default:
      return 'bg-neutral-100 text-neutral-600';
  }
};

export const formatActivityTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

export const calculateCompletionRate = (completed: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
};

// Both take a raw backend timestamp (Unix seconds); the seconds->Date
// conversion is delegated to the shared `timestampToDate` adapter so this is
// the only place in the dashboard feature that touches the raw timestamp.
export const formatTime = (timestamp: number): string =>
  formatTimeFromDate(timestampToDate(timestamp));

export const formatFullDate = (timestamp: number): string =>
  formatFullDateFromDate(timestampToDate(timestamp));

export const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const getScheduledCallStatusIcon = (status: string) => {
  switch (status) {
    case 'accepted':
      return CheckCircle;
    case 'rejected':
      return XCircle;
    default:
      return AlertCircle;
  }
};

export const getScheduledCallStatusText = (status: string): string => {
  switch (status) {
    case 'accepted':
      return 'Confirmed';
    case 'rejected':
      return 'Rejected';
    default:
      return 'Pending';
  }
};

export const getScheduledCallStatusColor = (status: string): string => {
  switch (status) {
    case 'accepted':
      return 'bg-green-50 border-green-200 text-green-700';
    case 'rejected':
      return 'bg-red-50 border-red-200 text-red-700';
    default:
      return 'bg-orange-50 border-orange-200 text-orange-700';
  }
};

export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

export const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const getCallsForDate = (calls: ScheduledCall[], date: Date): ScheduledCall[] => {
  return calls.filter((call) => isSameDay(timestampToDate(call.startTime), date));
};

export const formatRoleLabel = (role?: string): string => {
  if (!role) return '';
  return role.charAt(0).toUpperCase() + role.slice(1);
};

const formatFullName = (firstName?: string, lastName?: string, fallback = 'Unknown User'): string =>
  `${firstName || ''} ${lastName || ''}`.trim() || fallback;

export const mapCallToTodayItem = (
  call: ScheduledCall,
  onJoin: (id: string) => void,
  onSelectCall?: (call: ScheduledCall) => void
): DashboardListItem => ({
  id: call.id,
  title: formatFullName(call.student?.firstName, call.student?.lastName, 'Unknown Student'),
  subtitle: call.course?.name || 'Course Hearing',
  status: formatTime(call.startTime),
  statusColor: 'blue',
  type: 'hearing',
  onClick: () => (onSelectCall ? onSelectCall(call) : call.videoCallId && onJoin(call.videoCallId)),
});

export const mapProjectToPendingItem = (
  project: UserProject,
  onSelect: (project: UserProject) => void
): DashboardListItem => ({
  id: project.id.toString(),
  title: project.project?.name || 'Untitled Project',
  subtitle: formatFullName(project.user?.firstName, project.user?.lastName),
  type: 'project',
  onClick: () => onSelect(project),
});

export const mapCallToOverdueItem = (
  call: ScheduledCall,
  onJoin: (id: string) => void,
  onSelectCall?: (call: ScheduledCall) => void
): DashboardListItem => ({
  id: call.id,
  title: formatFullName(call.student?.firstName, call.student?.lastName, 'Unknown Student'),
  subtitle: call.course?.name || 'Course Hearing',
  status: 'Missed',
  statusColor: 'red',
  type: 'hearing',
  onClick: () => (onSelectCall ? onSelectCall(call) : call.videoCallId && onJoin(call.videoCallId)),
});

export const mapLateProjectToItem = (
  project: UserProject,
  onSelect: (project: UserProject) => void
): DashboardListItem => {
  const daysElapsed = Math.floor(
    (Date.now() - new Date(project.claimedAt!).getTime()) / MILLISECONDS_PER_DAY
  );
  const daysLate = daysElapsed - 5;
  return {
    id: project.id.toString(),
    title: project.project?.name || 'Untitled Project',
    subtitle: formatFullName(project.user?.firstName, project.user?.lastName),
    status: `${daysLate}d late`,
    statusColor: 'yellow',
    type: 'project',
    onClick: () => onSelect(project),
  };
};

export const mapOverdueProjectToItem = (
  project: UserProject,
  onSelect: (project: UserProject) => void
): DashboardListItem => {
  const daysElapsed = Math.floor(
    (Date.now() - new Date(project.claimedAt!).getTime()) / MILLISECONDS_PER_DAY
  );
  const daysOverdue = daysElapsed - 7;
  return {
    id: project.id.toString(),
    title: project.project?.name || 'Untitled Project',
    subtitle: formatFullName(project.user?.firstName, project.user?.lastName),
    status: `${daysOverdue}d overdue`,
    statusColor: 'red',
    type: 'project',
    onClick: () => onSelect(project),
  };
};
